import * as React from "react";
import { AlertDialog } from "radix-ui";
import { Button } from "./Button";
import { cn } from "./cn";
import { dialogBodyClass, dialogContentClass, dialogOverlayClass } from "./dialogShape";

// ConfirmDialog — the gate in front of a destructive action. Exists because the backend
// already declares `Danger: high` on 14 of the write routes these screens call (stop an app,
// delete a tenant, restore a snapshot over live data, impersonate a customer) and the UI was
// firing every one of them on a single click with no confirmation at all.
//
// `body` is a node, not a string, deliberately: the most useful confirmation is not "are you
// sure"but the SERVER'S OWN account of what is about to happen — e.g. GET /tenants/{id}/
// teardown already returns a preview of exactly what a delete would remove. Showing that is
// the Interlock principle applied to a destructive click: never confirm an action without
// showing what governs it.

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  pending?: boolean;
  /** Blocks the confirm button. Used where the caller wants the operator to prove intent — a
   *  typed acknowledgement — before a high-danger action can be issued. */
  confirmDisabled?: boolean;
  onConfirm(): void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = "Confirm",
  destructive = false,
  pending = false,
  confirmDisabled = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={dialogOverlayClass} />
        <AlertDialog.Content className={dialogContentClass}>
          <AlertDialog.Title className="px-5 pt-5 text-base font-semibold text-fg">
            {title}
          </AlertDialog.Title>
          {body && (
            <AlertDialog.Description asChild>
              <div className={cn(dialogBodyClass, "mt-3 px-5 text-sm text-muted")}>
                {body}
              </div>
            </AlertDialog.Description>
          )}
          <div className="mt-2 flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3.5">
            <AlertDialog.Cancel asChild>
              <Button variant="default" disabled={pending}>
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <Button
              variant={destructive ? "danger" : "accent"}
              disabled={pending || confirmDisabled}
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
            >
              {pending ? "Working…" : confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/** What a confirmation says: the act, its consequence, the word on the button. */
export interface ConfirmSpec {
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  /** Destructive unless said otherwise: this hook exists for the actions that cost something. */
  destructive?: boolean;
}

/**
 * useConfirm — the one way a screen puts a confirmation in front of an action.
 *
 *   const confirm = useConfirm();
 *   <Button variant="danger" onClick={() => confirm.ask({ title: "Delete x?", confirmLabel: "Delete" }, () => del(x))}>
 *   …
 *   {confirm.dialog}
 *
 * Every dangerous control in the customer consoles goes through this or through a ConfirmDialog
 * of its own; the source guard (kit/src/confirmGuard.test.ts) fails the build when one fires
 * straight from its click. The action runs when confirmed, and the dialog stays open showing
 * "Working…" until it settles, so a slow delete cannot be clicked twice.
 */
export function useConfirm(): { ask: (spec: ConfirmSpec, run: () => unknown) => void; dialog: React.ReactNode } {
  const [state, setState] = React.useState<{ spec: ConfirmSpec; run: () => unknown } | null>(null);
  const [pending, setPending] = React.useState(false);
  const ask = React.useCallback((spec: ConfirmSpec, run: () => unknown) => setState({ spec, run }), []);
  const dialog = (
    <ConfirmDialog
      open={state != null}
      onOpenChange={(o) => {
        if (!o && !pending) setState(null);
      }}
      title={state?.spec.title ?? ""}
      body={state?.spec.body}
      confirmLabel={state?.spec.confirmLabel ?? "Confirm"}
      destructive={state?.spec.destructive ?? true}
      pending={pending}
      onConfirm={async () => {
        if (!state) return;
        setPending(true);
        try {
          await state.run();
        } finally {
          setPending(false);
          setState(null);
        }
      }}
    />
  );
  return { ask, dialog };
}
